import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  Close,
  DeleteOutline,
  Edit,
  Groups,
  PersonAdd,
  Search,
  Schedule,
} from '@mui/icons-material';
import { batchesAPI, usersAPI } from '../services/api';
import PageHeader from '../components/PageHeader';
import { useAppData } from '../context/AppDataContext';

function getCurrentUser() {
  try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
}

function getGymId(value) {
  if (!value) return '';
  return typeof value === 'object' ? value._id : value;
}

function getGymName(gyms, value) {
  const gymId = getGymId(value);
  return gyms.find((gym) => gym._id === gymId)?.name || 'No gym';
}

function initials(name) {
  return String(name || 'B').trim().charAt(0).toUpperCase();
}

function uniqueById(items) {
  const seen = new Set();
  return (items || []).filter((item) => {
    if (!item?._id || seen.has(item._id)) return false;
    seen.add(item._id);
    return true;
  });
}

export default function Batches() {
  const { gyms } = useAppData();
  const currentUser = getCurrentUser();
  const isSuperadmin = currentUser?.role === 'superadmin';
  const [batches, setBatches] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);

  const filteredBatches = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return batches;
    return batches.filter((batch) =>
      batch.name?.toLowerCase().includes(needle) ||
      getGymName(gyms, batch.gymId).toLowerCase().includes(needle)
    );
  }, [batches, gyms, search]);

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [batchRes, userRes] = await Promise.all([
        batchesAPI.getAll(),
        usersAPI.getAll({ role: 'user', limit: 1000 }),
      ]);
      setBatches(batchRes.data.data || []);
      setUsers(userRes.data.data || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (payload) => {
    try {
      await batchesAPI.create(payload);
      setSuccess('Batch created successfully');
      setCreateOpen(false);
      loadData();
    } catch (e) {
      throw new Error(e.response?.data?.message || 'Failed to create batch');
    }
  };

  const materializeBatch = async (batch) => {
    if (!batch.legacy) return batch;
    const res = await batchesAPI.create({
      name: batch.name,
      gymId: getGymId(batch.gymId),
      preferredTime: batch.preferredTime || '',
    });
    return res.data.data;
  };

  const handleUpdate = async (batch, payload) => {
    try {
      const target = await materializeBatch(batch);
      await batchesAPI.update(target._id, payload);
      setSuccess('Batch updated successfully');
      setEditingBatch(null);
      loadData();
    } catch (e) {
      throw new Error(e.response?.data?.message || e.message || 'Failed to update batch');
    }
  };

  const handleRemoveMember = async (batch, userId) => {
    try {
      const target = await materializeBatch(batch);
      const res = await batchesAPI.update(target._id, { removeUserIds: [userId] });
      const updatedBatch = res.data.data;
      setEditingBatch(updatedBatch);
      setBatches((prev) => prev.map((item) => (
        item._id === batch._id || item._id === target._id ? updatedBatch : item
      )));
      setUsers((prev) => prev.map((user) => (
        user._id === userId ? { ...user, batchName: '', preferredTime: '' } : user
      )));
      setSuccess('Member removed from batch');
      loadData();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to remove member');
    }
  };

  return (
    <Box>
      <PageHeader
        title="Batches"
        subtitle="Create batches, set class time, and assign members from one place."
        gradient="linear-gradient(90deg, #0F766E, #2DD4BF)"
        actions={
          <Button variant="contained" size="small" startIcon={<Add />} onClick={() => setCreateOpen(true)}>Create Batch</Button>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2.5 }}>
        <TextField
          placeholder="Search batches or gyms..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ flexGrow: 1 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment> }}
        />
      </Stack>

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Batch</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Gym</TableCell>
              <TableCell>Preferred Time</TableCell>
              <TableCell>Members</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? [...Array(5)].map((_, index) => (
              <TableRow key={index}>
                <TableCell><Skeleton width={180} height={24} /></TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><Skeleton width={120} height={18} /></TableCell>
                <TableCell><Skeleton width={80} height={18} /></TableCell>
                <TableCell><Skeleton width={60} height={24} /></TableCell>
                <TableCell align="right"><Skeleton width={70} height={28} sx={{ ml: 'auto' }} /></TableCell>
              </TableRow>
            )) : filteredBatches.map((batch) => (
              <TableRow key={batch._id}>
                <TableCell>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.main', color: '#fff', fontSize: '0.875rem', fontWeight: 800 }}>
                      {initials(batch.name)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={700}>{batch.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {batch.legacy ? 'Imported from existing users' : 'Batch'}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <Typography variant="body2" color="text.secondary">{getGymName(gyms, batch.gymId)}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={<Schedule sx={{ fontSize: 15 }} />}
                    label={batch.preferredTime || 'Not set'}
                    size="small"
                    sx={{ 
                      fontWeight: 700, 
                      bgcolor: batch.preferredTime 
                        ? (theme) => theme.palette.mode === 'dark' ? 'rgba(37, 99, 235, 0.2)' : '#EFF6FF' 
                        : (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#F9FAFB', 
                      color: batch.preferredTime 
                        ? (theme) => theme.palette.mode === 'dark' ? '#93C5FD' : '#2563EB' 
                        : (theme) => theme.palette.mode === 'dark' ? '#9CA3AF' : '#6B7280',
                      border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : 'none'
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip icon={<Groups sx={{ fontSize: 15 }} />} label={batch.memberCount || 0} size="small" sx={{ fontWeight: 700 }} />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit batch">
                    <IconButton size="small" onClick={() => setEditingBatch(batch)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!loading && filteredBatches.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Box textAlign="center" py={5}>
                    <Groups sx={{ fontSize: 42, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">No batches found</Typography>
                    <Button variant="contained" startIcon={<Add />} sx={{ mt: 2 }} onClick={() => setCreateOpen(true)}>
                      Create Batch
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {createOpen && (
        <BatchFormDialog
          title="Create Batch"
          gyms={gyms}
          isSuperadmin={isSuperadmin}
          currentUser={currentUser}
          onClose={() => setCreateOpen(false)}
          onSave={handleCreate}
        />
      )}

      {editingBatch && (
        <BatchFormDialog
          title="Edit Batch"
          batch={editingBatch}
          gyms={gyms}
          users={users}
          isSuperadmin={isSuperadmin}
          currentUser={currentUser}
          onClose={() => setEditingBatch(null)}
          onSave={(payload) => handleUpdate(editingBatch, payload)}
          onRemoveMember={(userId) => handleRemoveMember(editingBatch, userId)}
        />
      )}
    </Box>
  );
}

function BatchFormDialog({ title, batch, gyms, users = [], isSuperadmin, currentUser, onClose, onSave, onRemoveMember }) {
  const [form, setForm] = useState({
    name: batch?.name || '',
    gymId: getGymId(batch?.gymId) || getGymId(currentUser?.gymId) || (gyms.length > 0 ? getGymId(gyms[0]) : ''),
    preferredTime: batch?.preferredTime || '',
    addUserIds: [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const members = uniqueById(batch?.members || []);
  const memberIds = new Set(members.map((member) => member._id));
  const availableUsers = uniqueById(users).filter((user) => {
    const userGymId = getGymId(user.gymId);
    return (
      !memberIds.has(user._id) &&
      !user.batchName &&
      (!form.gymId || userGymId === form.gymId)
    );
  });

  const update = (field, value) => {
    const nextValue = field === 'addUserIds' ? [...new Set(value)] : value;
    setForm((prev) => ({ ...prev, [field]: nextValue }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Batch name is required'); return; }
    if (!form.gymId) { setError('Gym is required'); return; }
    setSaving(true);
    try {
      await onSave({
        name: form.name.trim(),
        gymId: form.gymId,
        preferredTime: form.preferredTime,
        addUserIds: [...new Set(form.addUserIds)],
      });
    } catch (e) {
      setError(e.message || 'Failed to save batch');
      setSaving(false);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={onClose}><Close /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField label="Batch Name" value={form.name} onChange={(e) => update('name', e.target.value)} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Preferred Time"
              type="time"
              value={form.preferredTime}
              onChange={(e) => update('preferredTime', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            {isSuperadmin ? (
              <TextField select label="Gym" value={form.gymId} onChange={(e) => update('gymId', e.target.value)} fullWidth disabled={!!batch}>
                {gyms.map((gym) => <MenuItem key={gym._id} value={gym._id}>{gym.name}</MenuItem>)}
              </TextField>
            ) : (
              <TextField label="Gym" value={getGymName(gyms, form.gymId)} fullWidth disabled />
            )}
          </Grid>

          {batch && (
            <>
              <Grid item xs={12}>
                <TextField
                  select
                  label="Add Users"
                  value={form.addUserIds}
                  onChange={(e) => update('addUserIds', e.target.value)}
                  fullWidth
                  SelectProps={{ multiple: true }}
                  helperText="Only unassigned users are shown. Users already in a batch are hidden."
                  InputProps={{ startAdornment: <InputAdornment position="start"><PersonAdd fontSize="small" /></InputAdornment> }}
                >
                  {availableUsers.map((user) => (
                    <MenuItem key={user._id} value={user._id}>
                      {user.name} - {user.email}
                    </MenuItem>
                  ))}
                  {availableUsers.length === 0 && <MenuItem disabled>No available users</MenuItem>}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={700}>Current Members</Typography>
                    <Chip label={`${members.length || 0} users`} size="small" />
                  </Stack>
                  <Stack spacing={1}>
                    {members.map((member) => (
                      <Stack key={member._id} direction="row" spacing={1.5} alignItems="center" sx={{ px: 1.5, py: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Avatar sx={{ width: 30, height: 30, fontSize: '0.75rem', bgcolor: 'action.hover', color: 'text.primary' }}>{initials(member.name)}</Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>{member.name}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>{member.email}</Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">{member.preferredTime || form.preferredTime || 'No time'}</Typography>
                        {onRemoveMember && (
                          <Tooltip title="Remove from batch">
                            <IconButton size="small" color="error" onClick={() => onRemoveMember(member._id)}>
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    ))}
                    {members.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        No users in this batch yet.
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Batch'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
